/**
 * Composable for tracking custom events with Plausible Analytics
 */
export function usePlausible() {
  const PLAUSIBLE_API = 'https://plausible.io/api/event'
  const PLAUSIBLE_DOMAIN = 'kudosales.com'

  /**
   * Track a custom event using plausible.js (npm)
   */
  const trackEvent = async (
    eventName: string,
    props?: Record<string, any>
  ) => {
    // Get user email from auth store if available
    let userEmail = ''
    try {
      const { useAuthStore } = await import('@/store/auth')
      const auth = useAuthStore()
      userEmail = auth.user?.email || ''
    } catch (err) {
      // Ignore auth errors, continue with tracking
    }

    // Merge user email with provided props
    const enrichedProps = {
      ...props,
      email: userEmail || props?.email || ''
    }

    if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
      window.plausible(eventName, { props: enrichedProps })
    } else if (import.meta.dev) {
      console.warn('[Plausible] window.plausible not loaded, cannot track event:', eventName)
    }
  }

  /**
   * Track revenue using direct API POST for Plausible e-commerce
   * This sends the event with root-level "$" property, which is REQUIRED for revenue tracking!
   */
  const trackRevenue = async (
    amount: number,
    currency: string,
    email?: string,
    eventName = 'Checkout'
  ) => {
    const payload: Record<string, any> = {
      n: eventName,
      d: PLAUSIBLE_DOMAIN,
      u: window.location.href,
      r: document.referrer || null,
      $: { amount, currency },
      v: 32 // optional Plausible version
    }
   
    try {
      await fetch(PLAUSIBLE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload)
      })
      if (import.meta.dev) {
        console.log('[Plausible] Revenue event sent:', payload)
      }
    } catch (err) {
      if (import.meta.dev) {
        console.error('[Plausible] Revenue event failed:', err)
      }
    }
  }

  /**
   * Track a pageview manually (npm plausible.js only)
   */
  const trackPageview = async (props?: Record<string, any>) => {
    // Get user email from auth store if available
    let userEmail = ''
    try {
      const { useAuthStore } = await import('@/store/auth')
      const auth = useAuthStore()
      userEmail = auth.user?.email || ''
    } catch (err) {
      // Ignore auth errors, continue with tracking
    }

    // Merge user email with provided props
    const enrichedProps = {
      ...props,
      email: userEmail || props?.email || ''
    }

    if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
      window.plausible('pageview', { props: enrichedProps })
    } else if (import.meta.dev) {
      console.warn('[Plausible] window.plausible not loaded, cannot track pageview')
    }
  }

  return {
    trackEvent,
    trackPageview,
    trackRevenue
  }
}